/**
 * (Группа 3) Wizard онбординга отеля — контейнер 8 шагов.
 *
 * Навигация вперёд/назад с блокировкой перехода при невалидном текущем шаге
 * (canAdvanceFrom). Последний шаг (go-live) вызывает REAL
 * POST /v1/admin/provision-tenant-with-manager.
 */
import { useState } from 'react';
import { Button } from '@hsc/ui';
import { provisionTenantWithManager } from '../../lib/api.js';
import {
  EMPTY_WIZARD_DATA,
  WIZARD_STEPS,
  WIZARD_TOTAL_STEPS,
  canAdvanceFrom,
  nextStepIndex,
  prevStepIndex,
  type WizardData,
} from './wizardConfig.js';
import {
  StepBilling,
  StepBranding,
  StepGoLive,
  StepHotel,
  StepIntegrations,
  StepManager,
  StepSeed,
  StepTier,
} from './steps.js';

export function OnboardingWizardPage(): JSX.Element {
  const [index, setIndex] = useState(1); // 1-based
  const [data, setData] = useState<WizardData>(EMPTY_WIZARD_DATA);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneSlug, setDoneSlug] = useState<string | null>(null);

  const meta = WIZARD_STEPS[index - 1]!;
  const patch = (p: Partial<WizardData>): void => setData((d) => ({ ...d, ...p }));

  function goNext(): void {
    setError(null);
    if (!canAdvanceFrom(meta.id, data)) {
      setError('Заполните обязательные поля шага');
      return;
    }
    const next = nextStepIndex(index);
    if (next !== null) setIndex(next);
  }

  function goBack(): void {
    setError(null);
    const prev = prevStepIndex(index);
    if (prev !== null) setIndex(prev);
  }

  async function submit(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const res = await provisionTenantWithManager({
        hotel_name: data.hotelName,
        slug: data.slug,
        manager_email: data.managerEmail,
        manager_first_name: data.managerFirstName,
        manager_last_name: data.managerLastName,
        tier: data.tier,
        capabilities: data.capabilities,
      });
      setDoneSlug(res.slug);
    } catch {
      setError('Не удалось запустить отель. Проверьте данные и повторите.');
    } finally {
      setSubmitting(false);
    }
  }

  if (doneSlug) {
    return (
      <section className="hsc-page" data-testid="wizard-done">
        <h1 className="hsc-page__title">Отель подключён</h1>
        <p>Слаг: {doneSlug}. Управляющему отправлено приглашение.</p>
      </section>
    );
  }

  return (
    <section className="hsc-page" data-testid="onboarding-wizard">
      <h1 className="hsc-page__title">Онбординг отеля</h1>
      <p className="admin-wizard__progress" data-testid="wizard-progress">
        Шаг {meta.index} из {WIZARD_TOTAL_STEPS} — {meta.title}
      </p>

      <div className="admin-wizard__body">
        {meta.id === 'manager' && <StepManager data={data} patch={patch} />}
        {meta.id === 'hotel' && <StepHotel data={data} patch={patch} />}
        {meta.id === 'tier' && <StepTier data={data} patch={patch} />}
        {meta.id === 'branding' && <StepBranding />}
        {meta.id === 'integrations' && <StepIntegrations />}
        {meta.id === 'seed' && <StepSeed />}
        {meta.id === 'billing' && <StepBilling />}
        {meta.id === 'golive' && <StepGoLive data={data} />}
      </div>

      {error && (
        <div role="alert" className="hsc-input__error" data-testid="wizard-error">
          {error}
        </div>
      )}

      <div className="admin-wizard__nav">
        <Button variant="ghost" onClick={goBack} disabled={index === 1} data-testid="wizard-back">
          Назад
        </Button>
        {meta.id === 'golive' ? (
          <Button variant="primary" onClick={submit} loading={submitting} data-testid="wizard-submit">
            Запустить
          </Button>
        ) : (
          <Button variant="primary" onClick={goNext} data-testid="wizard-next">
            Далее
          </Button>
        )}
      </div>
    </section>
  );
}
